export const preparationTypes = [
  "sermon",
  "bible_study",
  "sunday_school",
  "devotional",
  "small_group",
  "youth_lesson",
] as const;

export type PreparationType = (typeof preparationTypes)[number];

export const sermonApproaches = [
  "expository",
  "topical",
  "textual",
  "narrative",
  "inductive",
] as const;

export type SermonApproach = (typeof sermonApproaches)[number];

export const sermonStatuses = [
  "idea",
  "draft",
  "ready",
  "preached",
  "archived",
] as const;

export type SermonStatus = (typeof sermonStatuses)[number];

export const sermonBlockTypes = [
  "title",
  "big_idea",
  "introduction",
  "point",
  "subpoint",
  "scripture",
  "explanation",
  "illustration",
  "application",
  "transition",
  "question",
  "quotation",
  "story",
  "prayer",
  "invitation",
  "private_note",
  "stage_direction",
] as const;

export type SermonBlockType = (typeof sermonBlockTypes)[number];

export type SermonRichText = {
  type: "doc";
  content: Array<Record<string, unknown>>;
};

export interface SermonBlock {
  id: string;
  type: SermonBlockType;
  content: SermonRichText;
  level?: 1 | 2 | 3 | undefined;
  collapsed?: boolean | undefined;
  includeInPreaching: boolean;
  manualMinutes?: number | null | undefined;
  scriptureContextId?: string | null | undefined;
  researchRunId?: string | undefined;
  sourceReferences?: Array<{
    kind: "scripture" | "deep_study" | "external_source";
    reference: string;
    sourceId?: string | undefined;
    verified: boolean;
  }> | undefined;
  provenance:
    | "user"
    | "scripture"
    | "external_source"
    | "interpretation"
    | "ai_suggestion";
}

export type SermonAiDraftMode = "quick" | "deep";
export type SermonAiDraftDepth = "outline" | "full";
export type SermonAiDraftStatus =
  | "queued"
  | "generating"
  | "ready"
  | "accepted"
  | "rejected"
  | "failed"
  | "canceled";

export interface SermonAiDraft {
  id: string;
  sermonId: string;
  mode: SermonAiDraftMode;
  depth: SermonAiDraftDepth;
  status: SermonAiDraftStatus;
  baseDocumentVersion: number;
  proposedDocument: SermonDocument | null;
  deepStudyId: string | null;
  researchRunId?: string | null;
  researchFocus: string;
  errorMessage: string | null;
  createdAt: number;
  updatedAt: number;
  completedAt: number | null;
  decidedAt: number | null;
}

export interface SermonDocument {
  schemaVersion: 1;
  blocks: SermonBlock[];
}

export interface SermonSeries {
  id: string;
  title: string;
  description: string;
  status: "active" | "completed" | "archived";
  sermonCount?: number;
  createdAt: number;
  updatedAt: number;
}

export interface SermonSummary {
  id: string;
  title: string;
  preparationType: PreparationType;
  passageLabel: string;
  seriesId: string | null;
  seriesTitle: string | null;
  audience: string;
  scheduledFor: number | null;
  targetMinutes: number;
  status: SermonStatus;
  approach: SermonApproach;
  topics: string[];
  wordCount: number;
  estimatedMinutes: number;
  documentVersion: number;
  createdAt: number;
  updatedAt: number;
}

export interface SermonRecord extends SermonSummary {
  speaker: string;
  location: string;
  primaryVersionId: string;
  editionHash: string;
  primaryReferences: Array<{
    book: string;
    bookIndex: number;
    chapter: number;
    verseStart: number;
    verseEnd: number;
  }>;
  document: SermonDocument;
  currentRevisionId: string | null;
}

export interface SermonRevision {
  id: string;
  sermonId: string;
  version: number;
  document: SermonDocument;
  metadata: Record<string, unknown>;
  reason: "manual" | "autosave" | "ai_acceptance" | "export" | "preached" | "conflict";
  createdAt: number;
}

export const sermonStudyClassifications = [
  "observation",
  "interpretation",
  "question",
  "cross_reference",
  "quotation",
  "illustration",
  "application",
  "reflection",
  "research_needed",
] as const;

export type SermonStudyClassification =
  (typeof sermonStudyClassifications)[number];

export interface SermonStudyLink {
  id: string;
  sermonId: string;
  noteId: string;
  noteRevisionId: string;
  noteTitle: string;
  classification: SermonStudyClassification;
  excerpt: string;
  createdAt: number;
}

export interface SermonSuggestion {
  researchRunId?:string|null;
  id: string;
  sermonId: string;
  targetBlockId: string | null;
  action: string;
  currentContent: SermonRichText | null;
  suggestedContent: SermonRichText;
  rationale: string;
  status: "pending" | "accepted" | "edited" | "rejected";
  createdAt: number;
  decidedAt: number | null;
}

export interface SermonReviewFinding {
  id: string;
  category:
    | "reference"
    | "quotation"
    | "grounding"
    | "flow"
    | "timing"
    | "balance";
  severity: "info" | "consider" | "important";
  title: string;
  detail: string;
  blockId?: string | null;
}

export interface SermonArtifact {
  id: string;
  sermonId: string;
  sourceRevisionId: string | null;
  kind: "handout" | "discussion_guide";
  title: string;
  contentMarkdown: string;
  status: "draft" | "final";
  createdAt: number;
  updatedAt: number;
}

export interface SermonPreachingSession {
  id: string;
  sermonId: string;
  revisionId: string | null;
  startedAt: number;
  endedAt: number | null;
  elapsedSeconds: number;
  displayMode: "scroll" | "page";
}

export function plainTextFromRichText(content: SermonRichText | null | undefined): string {
  if (!content) return "";
  const collect = (node: unknown): string => {
    if (!node || typeof node !== "object") return "";
    const value = node as { text?: unknown; content?: unknown[]; type?: unknown };
    const own = typeof value.text === "string" ? value.text : "";
    const children = Array.isArray(value.content)
      ? value.content.map(collect).join(value.type === "paragraph" ? "" : " ")
      : "";
    return `${own}${children}`;
  };
  return collect(content).replace(/\s+/g, " ").trim();
}

export function sermonDocumentText(document: SermonDocument): string {
  return document.blocks
    .map((block) => plainTextFromRichText(block.content))
    .filter(Boolean)
    .join("\n");
}

export function sermonWordCount(document: SermonDocument): number {
  const text = sermonDocumentText(document);
  return text ? text.split(/\s+/).length : 0;
}

export function sermonEstimatedMinutes(document: SermonDocument, wordsPerMinute = 130): number {
  const manual = document.blocks.reduce(
    (sum, block) => sum + (block.manualMinutes ?? 0),
    0,
  );
  const automaticBlocks = document.blocks.filter(
    (block) => !block.manualMinutes && block.includeInPreaching,
  );
  const automaticWords = automaticBlocks.reduce(
    (sum, block) =>
      sum +
      (plainTextFromRichText(block.content)
        ? plainTextFromRichText(block.content).split(/\s+/).length
        : 0),
    0,
  );
  return Math.max(1, Math.ceil(manual + automaticWords / wordsPerMinute));
}

export function richTextFromPlainText(text: string): SermonRichText {
  return {
    type: "doc",
    content: [
      {
        type: "paragraph",
        content: text ? [{ type: "text", text }] : [],
      },
    ],
  };
}

export function createSermonBlock(
  type: SermonBlockType,
  text = "",
  overrides: Partial<SermonBlock> = {},
): SermonBlock {
  return {
    id: crypto.randomUUID(),
    type,
    content: richTextFromPlainText(text),
    level: type === "subpoint" ? 2 : type === "point" ? 1 : undefined,
    collapsed: false,
    includeInPreaching: type !== "private_note",
    manualMinutes: null,
    provenance: type === "scripture" ? "scripture" : "user",
    ...overrides,
  };
}

export function createStarterSermonDocument(title = "Untitled sermon"): SermonDocument {
  return {
    schemaVersion: 1,
    blocks: [
      createSermonBlock("title", title),
      createSermonBlock("big_idea", ""),
      createSermonBlock("introduction", ""),
      createSermonBlock("point", ""),
      createSermonBlock("application", ""),
      createSermonBlock("prayer", ""),
    ],
  };
}
